import { EventsEnum } from 'types/events.enum';
import { EventCallback } from 'types/callback.interface';
import { compact } from 'utils/compact';
import { Nullable } from 'types/nullable';
import { INJECTED_UI_ID } from 'constants/ui.constants';
import Logger from '../utils/logger';
import { AuthTypes } from 'types/auth-types.enum';
import { DocumentModalOptions } from 'types/document-modal-options.enum';
import { IPCSerializable } from 'types/ipc.interface';
import { IpcMessage } from 'types/ipc.interface';
import { isObject } from 'utils/isObject';
import invariant from '../utils/invariant';
import { IFrameStyleOptions } from 'types/SDK.interface';
import { SdkConfig } from 'types/api.interface';

export interface RendererOptions {
  element?: HTMLElement;
  url: string;
}

export interface IRenderer {
  setRoot(dom: HTMLElement): void;
  setUrl(url: string): void;
  exec(message: IPCSerializable): void;
  on(event: EventsEnum, callback: EventCallback<any>): void;
  enableLogging(enabled: boolean): void;
  setAuth(authType: AuthTypes): void;
  render(styles?: IFrameStyleOptions): void;
}

class Renderer implements IRenderer {
  /**
   * EventsMap is like event bus
   * Keeps registered callbacks for each event
   */
  private _eventsMap: Map<EventsEnum, EventCallback<any>[]>;
  /**
   * DOM element where frame should be injected
   */
  private _dom: Nullable<HTMLElement>;
  /**
   * URL of the frame from where should be loaded
   */
  private _url?: string;
  /**
   * Route to open in from the url
   */
  private _portalRoute!: string;
  /**
   * ApplicationID that needs to be loaded
   */
  private _applicationId!: string;
  /**
   * StipulationType of the application, that need to be loaded
   */
  private _stipulation!: string;
  /**
   * ApplicantType of the application, that needs to be loaded
   */
  private _applicant!: string;
  /**
   * Frame DOM element
   */
  private _frame: Nullable<Window>;
  /**
   * Logger instance
   */
  private _logger: Logger;
  /**
   * Unexecuted commands
   */
  private _queue: IPCSerializable[] = [];

  /**
   * Modal Option to determine modal behaivor
   */
  private _documentModalOption!: DocumentModalOptions;

  /**
   * jwtToken
   */
  private _config: SdkConfig;

  private _queryParams: Record<string, any>;

  constructor() {
    this._eventsMap = new Map();
    this._dom = null;
    this._frame = null;
    this._logger = new Logger(false);
    this._config = {
      authType: AuthTypes.Tab,
      documentModalOption: DocumentModalOptions.None,
      applicant: '',
      stipulation: '',
    };
    this._queryParams = {};

    this.onEventReceived = this.onEventReceived.bind(this);
    window.addEventListener('message', this.onEventReceived);
  }
  /**
   * Initializes @property _portalRoute property
   * @param route {String}
   */
  set portalRoute(route: string) {
    this._portalRoute = route;
  }

  /**
   * Initializes @property _applicationId property
   * @param applicationId {String}
   */
  set applicationId(applicationId: string) {
    this._applicationId = applicationId;
  }

  /**
   * Initializes @property _modalOption property
   * @param documentModalOption {DocumentModalOptions}
   */
  set documentModalOption(documentModalOption: DocumentModalOptions) {
    const isValidURL = documentModalOption
      ? Object.values(DocumentModalOptions).some(v => v === documentModalOption)
      : true;
    invariant(isValidURL, 'documentModalOptions is not a valid enum');
    this._config.documentModalOption = documentModalOption;
  }

  /**
   * Initializes @property _applicant property
   * @param applicant {String}
   */
  set applicant(applicant: string) {
    this._config.applicant = applicant;
  }
  setQueryParam(key: string, value: any) {
    this._queryParams[key] = value;
  }

  /**
   * Initializes @property _stipulation property
   * @param stipulation {String}
   */
  set stipulation(stipulation: string) {
    this._config.stipulation = stipulation;
  }

  /**
   * Defines logs visibility
   * @param enabled {Boolean}
   */
  public enableLogging(enabled: boolean): void {
    this._logger.setEnabled(enabled);
  }

  /**
   * Defines the root where renderer should be injected
   * @param dom {HTMLElement}
   */
  public setRoot(dom: HTMLElement) {
    this._dom = dom;
  }

  /**
   * Defines SAML Login type
   * @param authType {AuthTypes}
   */
  public setAuth(authType: AuthTypes): void {
    this._config.authType = authType;
  }

  public setUrl(url: string): void {
    this._url = url;
    this.invokeListeners(EventsEnum.PartnerLoaded);
  }

  /**
   * Generates IPCSerializable instance by the given parameters
   * @param type {String}
   * @param payload {*}
   */
  protected _generateExecCommand(type: string, payload?: any): IPCSerializable {
    return new IpcMessage(type, payload);
  }

  /**
   * Inflates UI, with styles, attributes and persists
   * on the instance of the renderer
   */
  protected _inflateUI(frameStyles?: IFrameStyleOptions): HTMLElement {
    const frame = document.createElement('iframe');
    frame.style.width = frameStyles?.width ?? '100%';
    frame.style.height = frameStyles?.height ?? '100%';
    frame.style.border = 'none';
    frame.style.boxShadow = '0px 15px 15px 2px rgba(0, 0, 0, 0.27)';
    frame.style.backgroundColor = '#fff';
    frame.id = INJECTED_UI_ID;
    const queryParams = new URLSearchParams(this._queryParams);
    queryParams.append('sdk', 'true');
    queryParams.append('sdkConfig', JSON.stringify(this._config));

    frame.src = `${this._url}/${this._portalRoute}/${
      this._applicationId
    }?${queryParams.toString()}`;

    frame.onload = () => {
      this._frame = frame.contentWindow!;
      console.log('this._queue', this._queue);
      this._queue.forEach(message => this.exec(message));
      this._queue = [];
    };

    return frame;
  }

  /**
   * Invokes listeners of particular event listeners
   * @param event {EventsEnum}
   * @param params {unknown}
   */
  private invokeListeners(event: EventsEnum, ...params: unknown[]): void {
    if (!this._eventsMap.has(event)) {
      return;
    }
    const callbacks = this._eventsMap.get(event)!;
    callbacks.forEach(cb => cb(...params));
  }

  /**
   * Handles events coming from the rendered frame
   * @param event {MessageEvent}
   */
  private onEventReceived(event: MessageEvent) {
    if (event.origin !== this._url) {
      return;
    }
    const ipcMessage = IpcMessage.fromEvent(event);

    if (!ipcMessage) {
      return;
    }
    const { message, payload } = ipcMessage;
    const messageAsEvent = <EventsEnum>(message as unknown);

    if (!this._eventsMap.has(messageAsEvent)) {
      return;
    }

    const formattedPayload = isObject(payload)
      ? Object.values(payload)
      : [payload];

    if (message === EventsEnum.DocumentRequestedViaSMS) {
      this.invokeListeners(messageAsEvent, payload);
      return;
    }

    this.invokeListeners(messageAsEvent, ...formattedPayload);
  }

  /**
   * Register callback for particular event
   * @param event {EventsEnum}
   * @param callback {EventCallback}
   */
  public on(event: EventsEnum, callback: EventCallback<any>): void {
    const callbacksArr = this._eventsMap.has(event)
      ? this._eventsMap.get(event)
      : [];

    const callbacks = [...compact<EventCallback<any>>(callbacksArr), callback];

    this._eventsMap.set(event, callbacks);
  }

  /**
   * Execute command inside frame
   * @param command {String}
   */
  public exec(ipcMessage: IPCSerializable): void {
    const command = ipcMessage.serialize();
    if (!this._frame) {
      this._logger.warn(`
      frame is not initialized yet, but code is trying to execute
      a command: ${command}
      Message will be queued
      `);
      this._queue.push(ipcMessage);
      return;
    }
    this._logger.log(command);
    console.log('Sent Command', command);
    this._frame.postMessage(command, '*');
  }

  /**
   * Render frame
   */
  public render(styleOptions?: IFrameStyleOptions): void {
    if (!this._url) {
      this.on(EventsEnum.PartnerLoaded, () => this.render(styleOptions));
      this._logger.warn(`
        URL still not initialized,
        frame will be loaded immediately when the URL will be set
      `);
      return;
    }

    const element = this._inflateUI(styleOptions);

    if (!this._dom) {
      throw new Error('DOM is not set before rendering');
    }

    this._dom.appendChild(element);
  }
}

export default Renderer;
